import { and, count, eq, gt, inArray, isNotNull } from 'drizzle-orm'

import type { Database } from '../client'
import type { InsertPremium, SelectSeatEntitlement } from '../schema'
import { Membership, Premium, Program, SeatEntitlement } from '../schema'

/**
 * Turning an entitlement into real access. An entitlement on its own is only a quota — the school
 * becomes premium the same way a paying school does: one Membership plus `totalSeats` unassigned
 * Premium rows it later claims per student.
 */

export interface ProvisionedEntitlement {
  membershipId: string
  seats: number
}

/**
 * Provision an activated entitlement, mirroring the paid checkout flow (`core / new-membership`):
 * insert one Membership for the school, then `totalSeats` Premium rows with `childId` null.
 *
 * Seats carry BOTH `membershipId` — so every existing membership-scoped read (seat alerts, student
 * table, giveChildPremium) sees them with no changes — and `entitlementId`, the reporting scope key
 * the impact rollups group by. `direct_paid` links the internal invoice its entitlement already
 * minted; grant-family memberships have no invoice.
 *
 * Idempotent: a second call for an already-provisioned entitlement is a no-op, so trigger retries
 * and a manual admin activate can never double-issue seats.
 *
 * Needs a transaction-capable client — pass `dbPool`, not `db` (the neon-http driver has no
 * interactive transactions).
 */
export async function provisionEntitlement(
  db: Database,
  entitlement: SelectSeatEntitlement,
): Promise<ProvisionedEntitlement | null> {
  return db.transaction(async (tx) => {
    // Inside the transaction so a concurrent activate (trigger + manual admin click) can't both
    // pass the check and issue two sets of seats.
    const [existing] = await tx
      .select({ value: count() })
      .from(Premium)
      .where(eq(Premium.entitlementId, entitlement.id))
    if ((existing?.value ?? 0) > 0) return null

    const [membership] = await tx
      .insert(Membership)
      .values({
        invoiceId: entitlement.invoiceId ?? undefined,
        organizationId: entitlement.assignedSchoolId,
        seats: entitlement.totalSeats,
        startDate: entitlement.startDate,
        endDate: entitlement.endDate,
      })
      .returning()
    if (!membership) throw new Error(`Failed to create membership for entitlement ${entitlement.id}`)

    const seats: InsertPremium[] = Array.from({ length: entitlement.totalSeats }, () => ({
      organizationId: entitlement.assignedSchoolId,
      source: 'entitlement' as const,
      membershipId: membership.id,
      entitlementId: entitlement.id,
      startDate: entitlement.startDate,
      endDate: entitlement.endDate,
    }))
    await tx.insert(Premium).values(seats)

    return { membershipId: membership.id, seats: seats.length }
  })
}

export type ProvisioningConflict = 'entitlement'

/**
 * A provision may not land on a school that already has a LIVE entitlement.
 *
 * It deliberately does NOT block on an existing paid membership any more: a grant is its own pool
 * with its own window, so a scholarship stacking on top of 50 purchased seats is the normal case,
 * not a collision. The old membership check existed only because the seat surfaces could read a
 * single membership; they read every pool now.
 *
 * The entitlement check stays: two live entitlements on one school is far more likely a
 * double-provisioning mistake than an intent, and nothing yet distinguishes the two.
 *
 * @returns which kind of existing access blocks provisioning, or `null` when the school is clear
 */
export async function findProvisioningConflict(
  db: Database,
  organizationId: string,
): Promise<ProvisioningConflict | null> {
  const entitlement = await db.query.SeatEntitlement.findFirst({
    where: and(
      eq(SeatEntitlement.assignedSchoolId, organizationId),
      inArray(SeatEntitlement.status, ['draft', 'active']),
    ),
    columns: { id: true },
  })
  if (entitlement) return 'entitlement'

  return null
}

/**
 * End access provisioned by an entitlement at `at`: close its seats and the membership that carries
 * them. Used by revoke — expiry needs nothing because every row's `endDate` is already the
 * entitlement's `endDate` and lapses on its own.
 */
export async function endEntitlementAccess(db: Database, entitlementId: string, at: Date): Promise<void> {
  const memberships = await db
    .selectDistinct({ membershipId: Premium.membershipId })
    .from(Premium)
    .where(and(eq(Premium.entitlementId, entitlementId), isNotNull(Premium.membershipId)))

  await db
    .update(Premium)
    .set({ endDate: at })
    .where(and(eq(Premium.entitlementId, entitlementId), gt(Premium.endDate, at)))

  const membershipIds = memberships.map((m) => m.membershipId).filter((id): id is string => id !== null)
  if (membershipIds.length === 0) return

  await db
    .update(Membership)
    .set({ endDate: at })
    .where(and(inArray(Membership.id, membershipIds), gt(Membership.endDate, at)))
}

/** Take a program live once any of its entitlements activates. No-op unless it is still `draft`. */
export async function markProgramActive(db: Database, programId: string): Promise<void> {
  await db
    .update(Program)
    .set({ status: 'active' })
    .where(and(eq(Program.id, programId), eq(Program.status, 'draft')))
}

/**
 * Expire a program once none of its entitlements are live any more. No-op while any child is still
 * `draft` or `active`.
 */
export async function markProgramExpiredIfDone(db: Database, programId: string): Promise<boolean> {
  const [remaining] = await db
    .select({ value: count() })
    .from(SeatEntitlement)
    .where(and(eq(SeatEntitlement.programId, programId), inArray(SeatEntitlement.status, ['draft', 'active'])))
  if ((remaining?.value ?? 0) > 0) return false

  await db
    .update(Program)
    .set({ status: 'expired' })
    .where(and(eq(Program.id, programId), eq(Program.status, 'active')))
  return true
}
